/**
 * Select Ritual Organizers Modal
 * Modal for searching and selecting users and communities as ritual organizers
 * Uses shadcn Dialog + Checkbox + Avatar
 */

import {
  ArrowLeft,
  Building2,
  MoreVertical,
  Plus,
  RefreshCw,
  Search,
  Users as UsersIcon,
  X,
} from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { InviteAcceptedIcon, InviteDeclinedIcon, TrashIcon } from "@/components/icons"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  type SearchAllItem,
  searchCommunitiesAndUsers,
} from "@/features/communities/services/community.service"
import {
  useResendOrganizerInvite,
  useWithdrawOrganizerInvite,
} from "@/features/rituals/hooks/useRitualInvites"
import { INVITE_NOTE_MAX_LENGTH } from "@/features/rituals/types"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { useIsMobile } from "@/hooks/useIsMobile"
import { cn } from "@/lib"

export interface RitualOrganizer {
  id: string
  type: "user" | "community"
  name: string
  identifier: string | null // username or handle
  profilePictureUrl: string | null
  inviteNote?: string
  dataCount?: {
    followers?: number
    members?: number
  }
}

interface SelectRitualOrganizersModalProps {
  isOpen: boolean
  onClose: () => void
  onAddOrganizers: (organizers: RitualOrganizer[]) => void
  existingOrganizerIds?: string[]
  currentUserId?: string // Creator's user ID - cannot be added as organizer
  ritualId?: string // If provided, returns organizer status for this ritual
}

export function SelectRitualOrganizersModal({
  isOpen,
  onClose,
  onAddOrganizers,
  existingOrganizerIds = [],
  currentUserId,
  ritualId,
}: SelectRitualOrganizersModalProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [items, setItems] = useState<SearchAllItem[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [itemNotes, setItemNotes] = useState<Map<string, string>>(new Map())

  const { resendOrganizerInviteAsync, isResending } = useResendOrganizerInvite(ritualId || "")
  const { withdrawOrganizerInviteAsync, isWithdrawing } = useWithdrawOrganizerInvite(ritualId || "")

  // Refetch trigger - increment after resend/withdraw to refresh search results
  const [refetchKey, setRefetchKey] = useState(0)

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("")
      setDebouncedSearch("")
      setItems([])
      setPage(1)
      setTotalPages(0)
      setExpandedItems(new Set())
      setItemNotes(new Map())
    }
  }, [isOpen])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch organizers (communities and users)
  const fetchOrganizers = useCallback(
    async (pageNum: number, query: string, isLoadMore = false) => {
      setIsLoading(true)

      try {
        const result = await searchCommunitiesAndUsers({
          search: query || undefined,
          page: pageNum,
          limit: 20,
          ritualId,
        })

        if (isLoadMore) {
          setItems(prev => [...prev, ...result.items])
        } else {
          setItems(result.items)
        }

        setTotalPages(result.pagination.totalPages)
        setPage(pageNum)
      } catch (error) {
        console.error("Failed to fetch organizers:", error)
        if (!isLoadMore) {
          setItems([])
        }
      } finally {
        setIsLoading(false)
      }
    },
    [ritualId],
  )

  // Refetch when search query changes or refetchKey increments
  useEffect(() => {
    if (isOpen) {
      void refetchKey
      fetchOrganizers(1, debouncedSearch)
    }
  }, [debouncedSearch, isOpen, fetchOrganizers, refetchKey])

  // Infinite scroll handler
  const handleLoadMore = useCallback(() => {
    if (!isLoading && page < totalPages) {
      fetchOrganizers(page + 1, debouncedSearch, true)
    }
  }, [isLoading, page, totalPages, debouncedSearch, fetchOrganizers])

  const loadMoreRef = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore: page < totalPages,
    disabled: isLoading,
  })

  const toggleExpand = (itemId: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId)
    } else {
      newExpanded.add(itemId)
    }
    setExpandedItems(newExpanded)
  }

  const handleNoteChange = (itemId: string, note: string) => {
    const newNotes = new Map(itemNotes)
    newNotes.set(itemId, note)
    setItemNotes(newNotes)
  }

  const handleSendInvite = (item: SearchAllItem) => {
    // Convert to RitualOrganizer format and call onAddOrganizers
    const organizerToAdd: RitualOrganizer = {
      id: item.id,
      type: item.type,
      name: item.title,
      identifier: item.identifier,
      profilePictureUrl: item.coverImageUrl || null,
      inviteNote: itemNotes.get(item.id) || undefined,
      dataCount:
        item.type === "user" ? { followers: item.followerCount } : { members: item.memberCount },
    }
    onAddOrganizers([organizerToAdd])
    setExpandedItems(new Set())
  }

  const isMobile = useIsMobile()

  const content = (
    <>
      <div className="px-5 mt-5">
        <div className="relative">
          <Search
            size={20}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-stone placeholder:text-[#F2F0EBE6]"
          />
          <Input
            type="text"
            placeholder="Search users or communities"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={cn(
              "pl-11 rounded-[15px] pr-5 py-5 text-sm leading-6.25",
              isMobile ? "h-12" : "h-16.25",
            )}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-5">
        {isLoading && items.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brown"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <UsersIcon size={48} className="mx-auto mb-4 text-stone" />
            <p className="text-stone">
              {debouncedSearch ? "No results match your search" : "No organizers found"}
            </p>
          </div>
        ) : (
          <>
            <h3 className="text-base font-semibold text-stone px-5 p-7.5">Suggested results</h3>
            <div className="space-y-2 flex flex-col gap-3">
              {items.map(item => {
                const organizerStatus = item.organizerStatus
                const isCommunity = item.type === "community"
                const isCreator = item.id === currentUserId
                const isExistingInList = existingOrganizerIds.includes(item.id)
                const hasOrganizerStatus =
                  (organizerStatus !== null && organizerStatus !== undefined) || isExistingInList

                const getStatusLabel = (status: typeof organizerStatus) => {
                  switch (status) {
                    case "PENDING":
                      return "Invited"
                    case "Accepted":
                      return "Accepted"
                    case "DECLINED":
                      return "Declined"
                    default:
                      return null
                  }
                }

                const statusLabel = getStatusLabel(organizerStatus)

                return (
                  <div
                    key={item.id}
                    className={cn(
                      "rounded-md transition-colors",
                      expandedItems.has(item.id) && "bg-[#F2F0EB0D]",
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-center gap-3.75 px-5 pt-2 rounded-md transition-colors",
                        hasOrganizerStatus && " cursor-not-allowed",
                      )}
                    >
                      <div className="flex-shrink-0">
                        {item.coverImageUrl ? (
                          <Avatar
                            src={item.coverImageUrl || undefined}
                            fallback={item.title}
                            size="md"
                            className="w-12.75 h-12.75"
                          />
                        ) : (
                          <div className="w-12.75 h-12.75 rounded-full bg-themed flex items-center justify-center">
                            {isCommunity ? (
                              <Building2 size={20} className="text-stone" />
                            ) : (
                              <UsersIcon size={20} className="text-stone" />
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-charcoal truncate">{item.title}</p>
                        <p className="text-xs leading-5 text-stone truncate">
                          {item.identifier ? `@${item.identifier}` : item.description || item.bio}
                        </p>
                        {item.type === "user" && item.followerCount !== undefined && (
                          <p className="text-xs leading-5 text-stone">
                            {item.followerCount} followers
                          </p>
                        )}
                        {item.type === "community" && item.memberCount !== undefined && (
                          <p className="text-xs leading-5 text-stone">{item.memberCount} members</p>
                        )}
                      </div>

                      <div className="flex items-center gap-[15px]">
                        {isCreator ? null : hasOrganizerStatus ? (
                          <div className="flex items-center gap-2">
                            <span className="flex items-center gap-1.5 text-[14px] text-white">
                              {organizerStatus === "Accepted" ? (
                                <InviteAcceptedIcon size={22} />
                              ) : organizerStatus === "DECLINED" ? (
                                <InviteDeclinedIcon size={22} color="#C86757" />
                              ) : (
                                <img
                                  src={`${import.meta.env.VITE_ASSET_BASE_URL}add_person.svg`}
                                  alt=""
                                  className="w-6 h-6"
                                />
                              )}
                              {statusLabel || (isExistingInList ? "Added" : null)}
                            </span>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  className="shrink-0 p-1.5 hover:bg-stone/10 rounded-full transition-colors"
                                  title="Options"
                                >
                                  <MoreVertical size={14} className="text-stone" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {(organizerStatus === "PENDING" ||
                                  organizerStatus === "DECLINED") && (
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      if (item.ritualOrganiserId) {
                                        await resendOrganizerInviteAsync({
                                          organizerId: item.ritualOrganiserId,
                                          inviteNote: itemNotes.get(item.id),
                                        })
                                        setRefetchKey(k => k + 1)
                                      }
                                    }}
                                    disabled={isResending}
                                    className="cursor-pointer"
                                  >
                                    <RefreshCw size={14} className="mr-2" />
                                    Resend
                                  </DropdownMenuItem>
                                )}
                                {organizerStatus === "PENDING" && (
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      if (item.ritualOrganiserId) {
                                        await withdrawOrganizerInviteAsync(item.ritualOrganiserId)
                                        setRefetchKey(k => k + 1)
                                      }
                                    }}
                                    disabled={isWithdrawing}
                                    className="text-[#C86757] cursor-pointer"
                                  >
                                    <TrashIcon size={14} />
                                    Withdraw
                                  </DropdownMenuItem>
                                )}
                                {organizerStatus === "Accepted" && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      /* TODO: Handle remove from organizers */
                                    }}
                                    className="text-[#C86757] cursor-pointer"
                                  >
                                    <TrashIcon size={14} />
                                    Remove
                                  </DropdownMenuItem>
                                )}
                                {isExistingInList && !organizerStatus && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      /* Handle remove from organizers in create flow */
                                    }}
                                    className="text-[#C86757] cursor-pointer"
                                  >
                                    <TrashIcon size={14} />
                                    Remove
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : expandedItems.has(item.id) ? (
                          <button
                            onClick={() => toggleExpand(item.id)}
                            className="flex-shrink-0 p-2 hover:bg-stone/10 rounded-full transition-colors"
                          >
                            <X size={18} className="text-stone" />
                          </button>
                        ) : (
                          <button
                            onClick={() => !isCreator && toggleExpand(item.id)}
                            disabled={isCreator}
                            className={cn(
                              "flex-shrink-0 p-2 hover:bg-brown/10 rounded-full transition-colors",
                              isCreator && "opacity-50 cursor-not-allowed",
                            )}
                          >
                            <Plus size={18} className="text-stone" />
                          </button>
                        )}
                      </div>
                    </div>

                    {expandedItems.has(item.id) && (
                      <div className="px-5 pb-3 pt-2.5">
                        <div className={cn("flex gap-5", isMobile && "flex-col items-end gap-3")}>
                          <div className="flex-1 w-full">
                            <textarea
                              placeholder="Write a short note to invite this organizer to co-host your ritual…"
                              value={itemNotes.get(item.id) || ""}
                              onChange={e => {
                                if (e.target.value.length <= INVITE_NOTE_MAX_LENGTH) {
                                  handleNoteChange(item.id, e.target.value)
                                }
                              }}
                              maxLength={INVITE_NOTE_MAX_LENGTH}
                              className="text-sm leading-6.25 p-3.75 w-full resize-none rounded-[15px] bg-[#F2F0EB0D] border border-[#F2F0EB1A]"
                            />
                            <div className="flex justify-start mt-1">
                              <span className="text-xs text-stone">
                                {(itemNotes.get(item.id) || "").length}/{INVITE_NOTE_MAX_LENGTH}
                              </span>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleSendInvite(item)}
                            size="sm"
                            variant="default"
                            className="text-xs font-semibold bg-white w-fit text-[var(--background-dark)] h-12.5 px-7.25 py-3.75"
                          >
                            SEND
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}

        <div ref={loadMoreRef} className="h-1" />
      </div>
    </>
  )

  if (isMobile) {
    if (!isOpen) return null
    return (
      <div className="fixed inset-0 z-[60] flex flex-col bg-background">
        <div className="flex items-center justify-between w-full gap-3 px-4 py-3.5">
          <button
            onClick={onClose}
            className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors"
          >
            <ArrowLeft size={22} className="text-primary" />
          </button>
          <div className=" flex items-center justify-between">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors">
                  <MoreVertical size={20} className="text-primary" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="z-[70]">
                <DropdownMenuItem onClick={onClose} className="cursor-pointer">
                  Close
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <h1 className="text-base leading-5 px-5 font-semibold text-charcoal">Add Organizers</h1>
        {content}
      </div>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="max-w-[850px] max-h-[700px] flex flex-col p-0 gap-0 border border-[#F2F0EB4D] bg-[#FFFFFF0D]">
        <DialogHeader className="p-5 flex">
          <DialogTitle className="text-base leading-5 font-semibold text-charcoal">
            Add Organizers
          </DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  )
}
